<template lang="pug">
.clicks-volume(:class="{ 'clicks-volume--compact': compact, 'clicks-volume--disabled': disabled }")
	.clicks-volume__header
		label.clicks-volume__label.m-0(v-if="label", :for="inputId") {{ label }}
		span.clicks-volume__value {{ currentLabel }}
	.clicks-volume__control
		.clicks-volume__track-wrap
			.clicks-volume__rail(aria-hidden="true")
				.clicks-volume__fill(:style="{ width: fillPercent }")
			input.clicks-volume__input(
				:id="inputId",
				type="range",
				:min="min",
				:max="max",
				step="1",
				:value="normalized",
				:disabled="disabled",
				:aria-valuemin="min",
				:aria-valuemax="max",
				:aria-valuenow="normalized",
				:aria-valuetext="currentLabel",
				:aria-label="label || 'Clicks Volume'",
				@input="onInput"
			)
		.clicks-volume__ticks(aria-hidden="true")
			button.clicks-volume__tick(
				v-for="level in levels",
				:key="level.value",
				type="button",
				tabindex="-1",
				:class="{ active: level.value === normalized }",
				:style="{ left: tickPosition(level.value) }",
				:title="level.label",
				:disabled="disabled",
				@click="setValue(level.value)"
			)
				span.clicks-volume__tick-mark
				span.clicks-volume__tick-label(v-if="!compact") {{ level.value }}
</template>

<script>
import {
	CLICKS_VOLUME_DEFAULT,
	CLICKS_VOLUME_LEVELS,
	CLICKS_VOLUME_MAX,
	CLICKS_VOLUME_MIN,
	clampClicksVolume,
	getClicksVolumeLabel,
} from "@/utils/clicksVolume";

let clicksVolumeId = 0;

export default {
	name: "ClicksVolume",
	props: {
		value: {
			type: [Number, String],
			default: CLICKS_VOLUME_DEFAULT,
		},
		label: {
			type: String,
			default: "Clicks Volume",
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		compact: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		clicksVolumeId += 1;
		return {
			inputId: `clicks-volume-${clicksVolumeId}`,
			levels: CLICKS_VOLUME_LEVELS,
			min: CLICKS_VOLUME_MIN,
			max: CLICKS_VOLUME_MAX,
		};
	},
	computed: {
		normalized() {
			return clampClicksVolume(this.value);
		},
		currentLabel() {
			return getClicksVolumeLabel(this.normalized);
		},
		fillPercent() {
			const span = this.max - this.min;
			const pct = span > 0 ? ((this.normalized - this.min) / span) * 100 : 0;
			return `${pct}%`;
		},
	},
	methods: {
		tickPosition(value) {
			const span = this.max - this.min;
			const pct = span > 0 ? ((value - this.min) / span) * 100 : 0;
			return `${pct}%`;
		},
		emitValue(next) {
			const value = clampClicksVolume(next);
			this.$emit("input", value);
			this.$emit("update:modelValue", value);
			this.$emit("change", value);
		},
		onInput(event) {
			this.emitValue(event.target.value);
		},
		setValue(value) {
			if (this.disabled) {
				return;
			}
			this.emitValue(value);
		},
	},
};
</script>

<style scoped lang="scss">
.clicks-volume {
	min-width: 12rem;
}

.clicks-volume__header {
	align-items: baseline;
	display: flex;
	gap: 0.75rem;
	justify-content: space-between;
	margin-bottom: 0.35rem;

	&:empty {
		display: none;
	}
}

.clicks-volume__label {
	color: inherit;
	font-weight: 500;
}

.clicks-volume__value {
	color: var(--ds-color-text, #111827);
	font-size: 0.8125rem;
	font-weight: 600;
	white-space: nowrap;
}

.clicks-volume__control {
	position: relative;
}

.clicks-volume__track-wrap {
	height: 1.5rem;
	position: relative;
}

.clicks-volume__rail {
	background: #e8eaef;
	border-radius: 999px;
	height: 6px;
	left: 0;
	overflow: hidden;
	pointer-events: none;
	position: absolute;
	right: 0;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
}

.clicks-volume__fill {
	background: linear-gradient(90deg, #ffc97a 0%, #ffb047 100%);
	height: 100%;
	width: 0;
}

.clicks-volume__input {
	-webkit-appearance: none;
	appearance: none;
	background: transparent;
	cursor: pointer;
	height: 1.5rem;
	margin: 0;
	position: relative;
	width: 100%;
	z-index: 3;

	&:focus {
		outline: none;
	}

	&:focus-visible {
		outline: 2px solid rgba(255, 176, 71, 0.55);
		outline-offset: 2px;
	}

	&::-webkit-slider-runnable-track {
		background: transparent;
		height: 6px;
	}

	&::-moz-range-track {
		background: transparent;
		height: 6px;
	}

	&::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		background: #fff;
		border: 2px solid #ffb047;
		border-radius: 50%;
		box-shadow: 0 1px 3px rgba(17, 24, 39, 0.18);
		height: 16px;
		margin-top: -5px;
		width: 16px;
	}

	&::-moz-range-thumb {
		background: #fff;
		border: 2px solid #ffb047;
		border-radius: 50%;
		box-shadow: 0 1px 3px rgba(17, 24, 39, 0.18);
		height: 16px;
		width: 16px;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.55;
	}
}

.clicks-volume__ticks {
	height: 1.35rem;
	margin-top: 0.15rem;
	position: relative;
	width: 100%;
}

.clicks-volume__tick {
	align-items: center;
	background: transparent;
	border: 0;
	cursor: pointer;
	display: flex;
	flex-direction: column;
	padding: 0;
	position: absolute;
	transform: translateX(-50%);

	&:disabled {
		cursor: default;
	}
}

.clicks-volume__tick-mark {
	background: #c5cad3;
	border-radius: 1px;
	display: block;
	height: 8px;
	width: 2px;
}

.clicks-volume__tick.active .clicks-volume__tick-mark {
	background: #ffb047;
}

.clicks-volume__tick-label {
	color: var(--ds-color-text-muted, #6b7280);
	font-size: 0.6875rem;
	line-height: 1;
	margin-top: 0.2rem;
}

.clicks-volume--compact {
	min-width: 9.5rem;

	.clicks-volume__header {
		margin-bottom: 0.1rem;
	}

	.clicks-volume__label {
		font-size: 0.75rem;
	}

	.clicks-volume__value {
		font-size: 0.75rem;
	}

	.clicks-volume__track-wrap {
		height: 1.25rem;
	}

	.clicks-volume__ticks {
		height: 0.65rem;
	}

	.clicks-volume__tick-mark {
		height: 6px;
	}
}

.clicks-volume--disabled {
	opacity: 0.7;
}
</style>
